import { HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';

/**
 * The help affordance that sits beside a field label — the single place a description or a
 * warning is shown, so no field ever grows a second line and rows of inputs stay aligned.
 *
 * Visual only. The text must ALSO exist in the DOM for assistive tech, referenced by the
 * control's `aria-describedby`; the shared inputs render it `sr-only` alongside this icon.
 * A tooltip alone would hide the description from exactly the users who need it most.
 */
export function HelpTip({
  label,
  tone = 'default',
  side = 'right',
  ariaLabel = 'More information',
}: {
  label: ReactNode;
  /** `warning` tints the icon amber for a caution rather than a plain explanation. */
  tone?: 'default' | 'warning';
  /** Beside the label by default: a top tooltip on the first field covers the modal title. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  ariaLabel?: string;
}) {
  return (
    <Tooltip label={label} side={side}>
      <button
        type="button"
        aria-label={ariaLabel}
        // Keeps a modal's initial focus off the help icon: it sits before the field it
        // belongs to, and this tooltip opens on focus.
        data-modal-skip-focus
        /*
          Out of the tab run. It sits between a label and its field, so at tabIndex 0 it
          took a stop on the way to every input on the form — and the text it discloses is
          already announced with that field through `aria-describedby`, so a screen reader
          loses nothing. The cost is real and accepted: a sighted keyboard-only reader can no
          longer open the tooltip, which is why the sr-only twin is not optional.
        */
        tabIndex={-1}
        className={[
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-middle transition',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200',
          tone === 'warning'
            ? 'text-amber-500 hover:text-amber-600'
            : 'text-slate-400 hover:text-slate-600',
        ].join(' ')}
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}
